import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Smartphone,
  ShieldCheck,
  Cpu,
  Database,
  Server,
  ArrowRight,
  Play,
  RotateCcw,
  Sparkles,
  Zap,
  Lock,
  Layers,
  HardDrive,
  Activity,
  CheckCircle2,
  Workflow,
  Radio,
  Terminal,
} from "lucide-react";
import { openConsultationModal } from "./ConsultationModal";

/* =========================================================================
   5 SYSTEM LAYERS WITH HUMAN-CENTERED ARCHITECTURE BENEFITS
   ========================================================================= */

export const SYSTEM_STAGES = [
  {
    id: "client",
    stageNum: "01",
    name: "Client Interfaces",
    tagline: "Lightning-Fast Web & Mobile Frontends",
    shortTitle: "Client Tier",
    metricBadge: "< 0.4s FCP",
    summary:
      "We engineer responsive web applications and native iOS and Android apps for immediate visual response. Pages load in under 0.4 seconds, touch interactions stay smooth at 60 FPS, and your user data stays preserved even during spotty offline network connections.",
    pillars: [
      {
        icon: Zap,
        title: "Sub-Second Page Load",
        desc: "Pages render almost instantly so your visitors never abandon or bounce.",
        badge: "< 0.4s FCP",
      },
      {
        icon: Smartphone,
        title: "60 FPS Native Fluidity",
        desc: "Buttery-smooth gestures and animations on iOS, Android, and web.",
        badge: "Native UI",
      },
      {
        icon: HardDrive,
        title: "Offline Data Sync",
        desc: "Local SQLite database caching keeps working seamlessly even offline.",
        badge: "Zero Data Loss",
      },
    ],
    techStack: ["Next.js 14 SSR", "React 18", "Flutter Native", "TypeScript", "Tailwind CSS"],
    safeguard: "Optimistic UI updates with instant local SQLite transaction rollback on failure.",
  },
  {
    id: "edge",
    stageNum: "02",
    name: "Edge & Perimeter Gate",
    tagline: "Bank-Grade Perimeter Security",
    shortTitle: "Edge Security",
    metricBadge: "< 2ms Latency",
    summary:
      "All incoming traffic passes through global Anycast edge firewalls before reaching your core servers. We automatically screen malicious bots, mitigate volumetric DDoS attacks, and enforce end-to-end SSL encryption so client records and financial transactions remain 100% secure.",
    pillars: [
      {
        icon: ShieldCheck,
        title: "24/7 Attack Defense",
        desc: "Stops malicious bots and traffic surges before they hit your core servers.",
        badge: "OWASP Top 10",
      },
      {
        icon: Lock,
        title: "100% Encrypted Traffic",
        desc: "Enterprise TLS 1.3 encryption protecting every login and payment in flight.",
        badge: "TLS 1.3",
      },
      {
        icon: Zap,
        title: "Sub-2ms Edge Routing",
        desc: "Edge security checks execute in under 2ms without degrading user speed.",
        badge: "< 2ms Speed",
      },
    ],
    techStack: ["Cloudflare WAF", "Nginx Reverse Proxy", "OAuth 2.0", "JWT Signatures"],
    safeguard: "Automated rate limiting throttles malicious IP clusters in real time with zero false positives.",
  },
  {
    id: "services",
    stageNum: "03",
    name: "Microservices Core",
    tagline: "High-Speed Microservices Built to Scale",
    shortTitle: "Microservices",
    metricBadge: "14.2k Req/Sec",
    summary:
      "Your core business logic runs on isolated, modular microservices. Heavy background jobs and analytics calculations run asynchronously in dedicated worker pools, ensuring customer-facing pages never freeze or slow down during viral traffic spikes.",
    pillars: [
      {
        icon: Activity,
        title: "14,000+ Req/Sec Capacity",
        desc: "Engineered to absorb unexpected user surges without slowing down.",
        badge: "High Throughput",
      },
      {
        icon: Workflow,
        title: "Decoupled Worker Queues",
        desc: "Heavy tasks like email sending and PDF compilation run in background queues.",
        badge: "Async BullMQ",
      },
      {
        icon: CheckCircle2,
        title: "Automatic Circuit Breakers",
        desc: "Isolates unexpected errors so a single bug never takes down your whole system.",
        badge: "Fault-Tolerant",
      },
    ],
    techStack: ["NestJS Modular", "Python FastAPI", "BullMQ Queues", "Redis Engine"],
    safeguard: "Decoupled asynchronous worker pools with automatic exponential backoff retries.",
  },
  {
    id: "data",
    stageNum: "04",
    name: "Data & Cache Vault",
    tagline: "Zero Data Loss Storage & Instant Memory Cache",
    shortTitle: "Data & Cache",
    metricBadge: "99.4% Cache Hit",
    summary:
      "Your critical records are permanently protected with automated continuous transaction logging, real-time database replication, and strict ACID compliance. In-memory Redis caching delivers frequent database queries in less than a millisecond.",
    pillars: [
      {
        icon: Zap,
        title: "Sub-Millisecond Cache",
        desc: "In-memory Redis clusters return frequent database queries in under 1ms.",
        badge: "0.6ms Redis",
      },
      {
        icon: Database,
        title: "Continuous Backups",
        desc: "Continuous transaction logging with automated point-in-time recovery.",
        badge: "ACID Compliant",
      },
      {
        icon: Lock,
        title: "Encrypted Document Vault",
        desc: "Private S3 file storage with expiring signed URLs for sensitive records.",
        badge: "AES-256 Storage",
      },
    ],
    techStack: ["PostgreSQL 16", "Redis Cluster", "AWS S3 Blobs", "Prisma ORM"],
    safeguard: "Streaming WAL database replicas with automated point-in-time recovery and zero data loss.",
  },
  {
    id: "cloud",
    stageNum: "05",
    name: "Autonomous Cloud SRE",
    tagline: "99.98% Uptime Cloud with Self-Healing Infrastructure",
    shortTitle: "Cloud & SRE",
    metricBadge: "99.98% SLA",
    summary:
      "Your software runs in modern containerized cloud environments that self-heal automatically if a server instance fails. Automated auto-scaling provisions extra server capacity on demand, while blue/green pipelines deploy software updates with zero downtime.",
    pillars: [
      {
        icon: Server,
        title: "99.98% Uptime SLA",
        desc: "Redundant Kubernetes container pods guarantee continuous availability.",
        badge: "99.98% SLA",
      },
      {
        icon: Radio,
        title: "Zero-Downtime Releases",
        desc: "Deploy new features and security patches without user maintenance windows.",
        badge: "Blue/Green",
      },
      {
        icon: Activity,
        title: "Automated Auto-Scaling",
        desc: "Automatically launches extra server capacity during high-demand peak hours.",
        badge: "Auto-Heal Pods",
      },
    ],
    techStack: ["Kubernetes", "Docker Pods", "Prometheus", "Grafana", "Terraform"],
    safeguard: "Automated health checks with instant traffic diversion away from unhealthy pods.",
  },
];

/* =========================================================================
   VECTOR SVG HARDWARE & SOFTWARE COMPONENT ILLUSTRATIONS (PRESERVED)
   White by default for high contrast, vibrant orange on hover/active
   ========================================================================= */

function StageGraphic({ stageId, isColored }) {
  const strokeColor = isColored ? "#FF5A1F" : "#FFFFFF";
  const innerLine = isColored ? "#FF7A3D" : "#E4E4E7";
  const primaryFill = isColored ? "#FF5A1F" : "#FFFFFF";
  const subtleFill = isColored ? "rgba(255, 90, 31, 0.15)" : "rgba(255, 255, 255, 0.08)";
  const screenBg = isColored ? "#181412" : "#141418";

  if (stageId === "client") {
    return (
      <svg viewBox="0 0 120 100" className="w-full h-full transition-colors duration-300">
        {/* Laptop Frame */}
        <rect
          x="15"
          y="20"
          width="70"
          height="45"
          rx="5"
          fill={subtleFill}
          stroke={strokeColor}
          strokeWidth="2"
        />
        {/* Laptop Screen Content */}
        <rect x="20" y="25" width="60" height="35" rx="3" fill={screenBg} />
        <line x1="26" y1="33" x2="48" y2="33" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
        <line x1="26" y1="41" x2="65" y2="41" stroke={innerLine} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="26" y1="48" x2="55" y2="48" stroke={innerLine} strokeWidth="1.5" strokeLinecap="round" />
        {/* Mini Graph on Screen */}
        <path d="M 52 50 L 58 42 L 64 46 L 72 36" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Laptop Base */}
        <path d="M 8 65 L 92 65 L 86 70 L 14 70 Z" fill={primaryFill} opacity={isColored ? 1 : 0.85} />

        {/* Mobile Phone Overlap */}
        <rect
          x="75"
          y="35"
          width="32"
          height="52"
          rx="6"
          fill={subtleFill}
          stroke={strokeColor}
          strokeWidth="2"
        />
        <rect x="79" y="41" width="24" height="40" rx="3" fill={screenBg} />
        {/* Mobile App Wireframe */}
        <circle cx="91" cy="48" r="3" fill={primaryFill} />
        <line x1="84" y1="56" x2="98" y2="56" stroke={innerLine} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="84" y1="63" x2="95" y2="63" stroke={innerLine} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="84" y1="70" x2="92" y2="70" stroke={innerLine} strokeWidth="1.5" strokeLinecap="round" />
        {/* Home Indicator */}
        <line x1="87" y1="78" x2="95" y2="78" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" />

        {/* Signal Waves */}
        {isColored && (
          <circle cx="91" cy="28" r="6" fill="none" stroke="#FF5A1F" strokeWidth="1.5" strokeDasharray="2 2" className="animate-ping" opacity="0.7" />
        )}
      </svg>
    );
  }

  if (stageId === "edge") {
    return (
      <svg viewBox="0 0 120 100" className="w-full h-full transition-colors duration-300">
        {/* Outer Hexagonal Shield */}
        <polygon
          points="60,10 98,28 98,72 60,90 22,72 22,28"
          fill={subtleFill}
          stroke={strokeColor}
          strokeWidth="2"
        />
        {/* Inner Laser Shield */}
        <polygon
          points="60,20 88,34 88,66 60,80 32,66 32,34"
          fill={screenBg}
          stroke={innerLine}
          strokeWidth="1.5"
          strokeDasharray={isColored ? "none" : "3 3"}
        />

        {/* Central Padlock / WAF Core */}
        <rect x="48" y="44" width="24" height="20" rx="3" fill={primaryFill} />
        <path
          d="M 53 44 L 53 36 C 53 30 67 30 67 36 L 67 44"
          fill="none"
          stroke={strokeColor}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="60" cy="54" r="2.5" fill={isColored ? "#FFFFFF" : "#0A0A0C"} />

        {/* Radar Scanning Line */}
        {isColored && (
          <line
            x1="32"
            y1="50"
            x2="88"
            y2="50"
            stroke="#FF5A1F"
            strokeWidth="2"
            strokeDasharray="4 4"
            className="animate-pulse"
          />
        )}
      </svg>
    );
  }

  if (stageId === "services") {
    return (
      <svg viewBox="0 0 120 100" className="w-full h-full transition-colors duration-300">
        {/* Microchip Main Body */}
        <rect
          x="30"
          y="20"
          width="60"
          height="60"
          rx="6"
          fill={subtleFill}
          stroke={strokeColor}
          strokeWidth="2"
        />

        {/* Chip Pins: Top & Bottom */}
        {[38, 48, 58, 68, 78].map((x) => (
          <g key={x}>
            <line x1={x} y1="12" x2={x} y2="20" stroke={strokeColor} strokeWidth="2" />
            <line x1={x} y1="80" x2={x} y2="88" stroke={strokeColor} strokeWidth="2" />
          </g>
        ))}

        {/* Chip Pins: Left & Right */}
        {[28, 38, 48, 58, 68].map((y) => (
          <g key={y}>
            <line x1="22" y1={y} x2="30" y2={y} stroke={strokeColor} strokeWidth="2" />
            <line x1="90" y1={y} x2="98" y2={y} stroke={strokeColor} strokeWidth="2" />
          </g>
        ))}

        {/* 4 Internal Microservice Cores */}
        <rect x="38" y="28" width="18" height="18" rx="3" fill={primaryFill} />
        <rect x="64" y="28" width="18" height="18" rx="3" fill={subtleFill} stroke={strokeColor} strokeWidth="1.5" />
        <rect x="38" y="54" width="18" height="18" rx="3" fill={subtleFill} stroke={strokeColor} strokeWidth="1.5" />
        <rect x="64" y="54" width="18" height="18" rx="3" fill={primaryFill} />

        {/* Circuit Interconnect Traces */}
        <circle cx="60" cy="50" r="3.5" fill={primaryFill} />
        <line x1="56" y1="37" x2="64" y2="37" stroke={innerLine} strokeWidth="1.5" strokeDasharray="1 1" />
        <line x1="56" y1="63" x2="64" y2="63" stroke={innerLine} strokeWidth="1.5" strokeDasharray="1 1" />
      </svg>
    );
  }

  if (stageId === "data") {
    return (
      <svg viewBox="0 0 120 100" className="w-full h-full transition-colors duration-300">
        {/* Top Cylinder Disc */}
        <ellipse cx="60" cy="26" rx="36" ry="12" fill={subtleFill} stroke={strokeColor} strokeWidth="2" />
        {/* Middle Cylinder Tier */}
        <path
          d="M 24 26 L 24 48 C 24 55 96 55 96 48 L 96 26"
          fill={screenBg}
          stroke={strokeColor}
          strokeWidth="2"
        />
        <ellipse cx="60" cy="48" rx="36" ry="10" fill={subtleFill} stroke={strokeColor} strokeWidth="1.5" />

        {/* Bottom Cylinder Tier */}
        <path
          d="M 24 48 L 24 70 C 24 77 96 77 96 70 L 96 48"
          fill={screenBg}
          stroke={strokeColor}
          strokeWidth="2"
        />
        <ellipse cx="60" cy="70" rx="36" ry="10" fill={subtleFill} stroke={strokeColor} strokeWidth="1.5" />

        {/* Activity Indicator LED Lights */}
        <circle cx="36" cy="38" r="3" fill={primaryFill} />
        <circle cx="44" cy="38" r="3" fill={isColored ? "#10B981" : "#FFFFFF"} />
        <circle cx="36" cy="60" r="3" fill={primaryFill} />
        <circle cx="44" cy="60" r="3" fill={isColored ? "#10B981" : "#FFFFFF"} />

        {/* Flow Query Sync Arrow */}
        {isColored && (
          <path
            d="M 80 34 L 84 40 L 76 40 Z"
            fill="#FF5A1F"
            className="animate-bounce"
          />
        )}
      </svg>
    );
  }

  // Cloud / SRE Tier
  return (
    <svg viewBox="0 0 120 100" className="w-full h-full transition-colors duration-300">
      {/* Central Cloud Bubble */}
      <path
        d="M 40 68 L 84 68 C 93 68 100 61 100 52 C 100 44 94 38 86 37 C 85 24 73 15 60 15 C 48 15 38 23 35 34 C 27 36 21 43 21 51 C 21 60 29 68 40 68 Z"
        fill={subtleFill}
        stroke={strokeColor}
        strokeWidth="2"
      />

      {/* Kubernetes Helm Hub inside Cloud */}
      <circle cx="60" cy="46" r="10" fill={screenBg} stroke={strokeColor} strokeWidth="2" />
      <circle cx="60" cy="46" r="4" fill={primaryFill} />

      {/* Orbiting Satellite Pod Nodes */}
      <circle cx="22" cy="26" r="5" fill={subtleFill} stroke={strokeColor} strokeWidth="2" />
      <line x1="26" y1="29" x2="36" y2="38" stroke={innerLine} strokeWidth="1.5" strokeDasharray="2 2" />

      <circle cx="98" cy="28" r="5" fill={subtleFill} stroke={strokeColor} strokeWidth="2" />
      <line x1="94" y1="31" x2="84" y2="39" stroke={innerLine} strokeWidth="1.5" strokeDasharray="2 2" />

      {/* Radar Radiating Rings */}
      {isColored && (
        <circle cx="60" cy="46" r="22" fill="none" stroke="#FF5A1F" strokeWidth="1.5" strokeDasharray="3 3" className="animate-spin" opacity="0.6" />
      )}
    </svg>
  );
}

export function SystemArchitectureConsole() {
  const [activeStageId, setActiveStageId] = useState("client");
  const [hoveredStageId, setHoveredStageId] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [activePacketIndex, setActivePacketIndex] = useState(null);

  const activeStage =
    SYSTEM_STAGES.find((s) => s.id === activeStageId) || SYSTEM_STAGES[0];

  // Auto-Simulation Flow
  useEffect(() => {
    let timer;
    if (isSimulating) {
      timer = setInterval(() => {
        setActiveStageId((currId) => {
          const currIdx = SYSTEM_STAGES.findIndex((s) => s.id === currId);
          const nextIdx = (currIdx + 1) % SYSTEM_STAGES.length;
          return SYSTEM_STAGES[nextIdx].id;
        });
      }, 2400);
    }
    return () => clearInterval(timer);
  }, [isSimulating]);

  // Transmit Single Packet Sequence
  const transmitPacket = () => {
    setIsSimulating(false);
    let step = 0;
    setActivePacketIndex(step);
    setActiveStageId(SYSTEM_STAGES[step].id);

    const stepInterval = setInterval(() => {
      step += 1;
      if (step < SYSTEM_STAGES.length) {
        setActivePacketIndex(step);
        setActiveStageId(SYSTEM_STAGES[step].id);
      } else {
        clearInterval(stepInterval);
        setActivePacketIndex(null);
      }
    }, 450);
  };

  return (
    <section
      id="architecture-topology"
      className="py-24 lg:py-32 bg-[#08080A] text-white relative section-dark overflow-hidden"
    >
      {/* Technical Blueprint Ambient Grid Background */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Center Radiant Spotlight */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[550px] rounded-full pointer-events-none opacity-20"
        style={{
          background:
            "radial-gradient(circle, rgba(255, 90, 31, 0.45) 0%, rgba(8, 8, 10, 0) 75%)",
          filter: "blur(130px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* ===================================================================
            HEADER: SENIOR ARCHITECTURE BANNER
            =================================================================== */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 lg:mb-16 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono uppercase tracking-widest text-neutral-300 mb-4 backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-[#FF5A1F] animate-pulse" />
              <span>Interactive System Blueprint</span>
              <span className="text-neutral-500 font-mono">/</span>
              <span className="text-[#FF5A1F] font-semibold">5 Decoupled Stages</span>
            </div>

            <h2 className="font-display font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              How We Architect Resilient Software Systems.
            </h2>

            <p className="text-sm sm:text-base text-neutral-400 mt-3 font-sans leading-relaxed">
              Hover or click any architectural node below to inspect data transmission routes, microsecond latency targets, and failover guarantees.
            </p>
          </div>

          {/* Interactive Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={transmitPacket}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-mono font-semibold bg-white/5 hover:bg-white/10 text-white border border-white/15 hover:border-white/30 transition-all cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#FF5A1F]" />
              <span>Transmit Packet</span>
            </button>

            <button
              onClick={() => setIsSimulating(!isSimulating)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer border ${
                isSimulating
                  ? "bg-[#FF5A1F] text-white border-[#FF5A1F] shadow-[0_0_20px_rgba(255,90,31,0.45)]"
                  : "bg-white/5 hover:bg-white/10 text-white border-white/15 hover:border-white/30"
              }`}
            >
              {isSimulating ? (
                <>
                  <RotateCcw className="w-3.5 h-3.5 animate-spin" />
                  <span>Pause Stream</span>
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 text-[#FF5A1F] fill-[#FF5A1F]" />
                  <span>Auto Flow</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ===================================================================
            CREATIVE VISUAL PIPELINE: 5 BESPOKE INTERACTIVE NODES (USER LIKES THIS!)
            Connected with animated fiber laser lines and packet runners
            =================================================================== */}
        <div className="relative mb-10 p-6 sm:p-8 rounded-3xl bg-[#0E0E12]/90 border border-white/10 backdrop-blur-2xl shadow-2xl overflow-x-auto scrollbar-none">
          {/* Animated Connecting Data Highway (Desktop) */}
          <div className="hidden lg:block absolute left-20 right-20 top-[102px] h-[2px] bg-white/10 pointer-events-none z-0">
            {/* Animated Laser Gradient Beam */}
            <div
              className="h-full bg-gradient-to-r from-[#FF5A1F] via-[#38BDF8] to-[#10B981] opacity-70"
              style={{
                boxShadow: "0 0 12px rgba(255, 90, 31, 0.8)",
              }}
            />
          </div>

          {/* 5 Distinct Hardware/Software Stages */}
          <div className="flex items-center justify-between min-w-[880px] gap-4 relative z-10">
            {SYSTEM_STAGES.map((stage, index) => {
              const isSelected = activeStage.id === stage.id;
              const isHovered = hoveredStageId === stage.id;
              const isColored = isSelected || isHovered;
              const isPacketHere = activePacketIndex === index;

              return (
                <div
                  key={stage.id}
                  onClick={() => {
                    setIsSimulating(false);
                    setActiveStageId(stage.id);
                  }}
                  onMouseEnter={() => {
                    setHoveredStageId(stage.id);
                    if (!isSimulating) setActiveStageId(stage.id);
                  }}
                  onMouseLeave={() => setHoveredStageId(null)}
                  className="flex-1 flex flex-col items-center cursor-pointer group"
                >
                  {/* Top Stage Indicator Chip */}
                  <div
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-mono uppercase font-bold tracking-wider mb-4 transition-all duration-300 ${
                      isColored
                        ? "bg-[#FF5A1F] text-white shadow-[0_0_15px_rgba(255,90,31,0.5)] scale-105"
                        : "bg-white/10 text-white border border-white/20 group-hover:bg-[#FF5A1F] group-hover:border-[#FF5A1F]"
                    }`}
                  >
                    <span>Stage {stage.stageNum}</span>
                    {isPacketHere && (
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                    )}
                  </div>

                  {/* Creative Visual Node Box */}
                  <motion.div
                    whileHover={{ y: -6, scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className={`w-28 h-28 sm:w-32 sm:h-32 rounded-3xl p-3 grid place-items-center relative transition-all duration-300 ${
                      isColored
                        ? "bg-[#181820] border-2 border-[#FF5A1F] shadow-[0_12px_36px_-6px_rgba(255,90,31,0.4)]"
                        : "bg-[#141418] border-2 border-white/25 hover:border-[#FF5A1F] shadow-lg"
                    }`}
                  >
                    {/* Glowing Aura Behind Active/Hovered Node */}
                    {isColored && (
                      <div
                        className="absolute inset-0 rounded-3xl pointer-events-none opacity-30"
                        style={{
                          background:
                            "radial-gradient(circle, #FF5A1F 0%, transparent 70%)",
                          filter: "blur(12px)",
                        }}
                      />
                    )}

                    {/* Vector Graphic: Crisp White by default, vibrant orange on hover/active */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 relative z-10">
                      <StageGraphic stageId={stage.id} isColored={isColored} />
                    </div>

                    {/* Active Ping Dot */}
                    <div className="absolute top-2.5 right-2.5">
                      <span
                        className={`w-2 h-2 rounded-full block transition-colors ${
                          isColored
                            ? "bg-[#FF5A1F] animate-pulse"
                            : "bg-white"
                        }`}
                      />
                    </div>
                  </motion.div>

                  {/* Title & Metric Preview */}
                  <div className="text-center mt-3.5">
                    <h4
                      className={`font-display text-xs sm:text-sm font-bold transition-colors ${
                        isColored ? "text-[#FF5A1F]" : "text-white group-hover:text-[#FF5A1F]"
                      }`}
                    >
                      {stage.name}
                    </h4>

                    <span
                      className={`font-mono text-[10px] sm:text-[11px] block mt-0.5 font-semibold transition-colors ${
                        isColored ? "text-[#FF5A1F]" : "text-neutral-300 group-hover:text-white"
                      }`}
                    >
                      {stage.metricBadge}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===================================================================
            REDESIGNED ARCHITECTURE SPECIFICATION CARD (CLEAN & ELEGANT)
            Replaces the cluttered technical HUD with clear, human-first value
            =================================================================== */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStage.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="rounded-3xl border border-white/15 bg-[#121216]/95 backdrop-blur-2xl p-7 sm:p-10 shadow-2xl relative overflow-hidden"
          >
            {/* Ambient Background Glow */}
            <div
              className="absolute -top-24 -right-24 w-80 h-80 rounded-full pointer-events-none opacity-20"
              style={{
                background:
                  "radial-gradient(circle, #FF5A1F 0%, transparent 70%)",
                filter: "blur(60px)",
              }}
            />

            {/* Header: Stage Tag, Live Status, Title, and Plain English Summary */}
            <div className="max-w-4xl mb-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/30 text-[#FF5A1F] font-mono text-xs font-bold uppercase tracking-wider">
                  Stage {activeStage.stageNum} · {activeStage.shortTitle}
                </span>
                <span className="text-xs font-mono text-emerald-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                  Active Layer
                </span>
              </div>

              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
                {activeStage.tagline}
              </h3>

              <p className="text-sm sm:text-base text-neutral-300 font-sans leading-relaxed mt-2.5">
                {activeStage.summary}
              </p>
            </div>

            {/* 3 Clear Architecture Pillars (Clean, spacious cards with icons) */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {activeStage.pillars.map((pillar, pIdx) => {
                const PillarIcon = pillar.icon;
                return (
                  <div
                    key={pIdx}
                    className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#FF5A1F]/40 transition-all duration-200 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-[#FF5A1F] grid place-items-center group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors">
                          <PillarIcon className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-neutral-300 border border-white/10">
                          {pillar.badge}
                        </span>
                      </div>

                      <h4 className="font-display text-sm font-bold text-white mb-1.5 group-hover:text-[#FF5A1F] transition-colors">
                        {pillar.title}
                      </h4>
                      <p className="text-xs text-neutral-400 font-sans leading-relaxed">
                        {pillar.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Row: Tech Stack Chips & Direct Consultation Trigger */}
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-mono text-neutral-400 uppercase tracking-wider mr-1">
                  Stack:
                </span>
                {activeStage.techStack.map((tech, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-mono px-3 py-1.5 rounded-xl bg-white/5 text-neutral-200 border border-white/10 hover:border-white/25 transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <button
                onClick={() =>
                  openConsultationModal(
                    `Architecture Consultation: ${activeStage.name}`
                  )
                }
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff7038] transition-all cursor-pointer shadow-lg shadow-[#FF5A1F]/25 flex-shrink-0 group"
              >
                <span>Discuss This Layer Architecture</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

export default SystemArchitectureConsole;
